'use client';

import { useState } from 'react';
import Link from 'next/link';
import { useZodForm } from '@/shared/validation';
import { z } from 'zod';
import { emailSchema } from '@/shared/validation';
import { SubmitHandler } from 'react-hook-form';
import { Poppins } from 'next/font/google';
import { BRAND_BLUE, BRAND_GREEN, BRAND_YELLOW } from '@voley/shared-types/constants';

const poppins = Poppins({ subsets: ['latin'], weight: ['700'] });

// Integration request schema
const integrationRequestSchema = z.object({
  name: z.string().min(1, 'Name is required').trim(),
  email: emailSchema,
  company: z.string().min(1, 'Company name is required').trim(),
  phone: z.string().optional(),
  integration: z.string().min(1, 'Please specify which integration you need').trim(),
  description: z.string().min(10, 'Please provide more details (at least 10 characters)').max(500, 'Description must be less than 500 characters').trim(),
});

type IntegrationRequestFormData = z.infer<typeof integrationRequestSchema>;

export default function RequestIntegrationPage() {
  const primaryColor = BRAND_BLUE;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useZodForm(integrationRequestSchema, {
    defaultValues: {
      name: '',
      email: '',
      company: '',
      phone: '',
      integration: '',
      description: '',
    }
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const onSubmit: SubmitHandler<IntegrationRequestFormData> = async (data) => {
    setIsSubmitting(true);

    try {
      // Send to external requests API
      const response = await fetch('/api/public/external-requests/integration', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to submit integration request');
      }

      await response.json();
      setIsSubmitted(true);
    } catch (_error) {
      // Error handling - form should show error state
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="bg-white">
        {/* Hero Section */}
        <div className="bg-white py-12 sm:py-16 md:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1
                className={`text-5xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-9xl ${poppins.className} tracking-tight leading-tight`}
              >
                <span className="block font-medium" style={{ color: '#111827' }}>
                  Thank you!
                </span>
              </h1>
              <div className="mt-6 flex items-center justify-center gap-3">
                <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: BRAND_BLUE }} />
                <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: BRAND_GREEN }} />
                <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: BRAND_YELLOW }} />
              </div>
            </div>
          </div>
        </div>

        {/* Success message */}
        <div className="bg-gray-50 py-16">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-8 sm:p-10 text-center">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full mb-6" style={{ backgroundColor: 'rgba(134, 208, 157, 0.2)' }}>
                <svg className="h-8 w-8" style={{ color: BRAND_GREEN }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Request Received!</h2>
              <p className="text-gray-600 mb-8">
                Thank you for your integration request. Our development team will review your request and reach out to discuss next steps and timeline.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  href="/"
                  className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-full text-white transition-all hover:transform hover:scale-[1.02] hover:shadow-lg"
                  style={{ backgroundColor: primaryColor }}
                >
                  Return to Home
                </Link>
                <Link
                  href="/request-demo"
                  className="inline-flex items-center justify-center px-6 py-3 border border-gray-300 text-base font-medium rounded-full text-gray-700 bg-white transition-all hover:transform hover:scale-[1.02] hover:shadow-lg hover:bg-gray-50"
                >
                  Schedule a Demo
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <div className="bg-white py-12 sm:py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1
              className={`text-5xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-9xl ${poppins.className} tracking-tight leading-tight`}
            >
              <span className="block font-medium" style={{ color: '#111827' }}>
                Request your integration.
              </span>
            </h1>
            <p className="mt-4 sm:mt-6 max-w-2xl mx-auto text-base sm:text-lg md:text-xl lg:text-2xl font-light text-gray-600 px-2">
              Let us know which AMS, CRM, or other system you&apos;d like Voley to integrate with next.
            </p>
            <div className="mt-6 flex items-center justify-center gap-3">
              <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: BRAND_BLUE }} />
              <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: BRAND_GREEN }} />
              <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: BRAND_YELLOW }} />
            </div>
          </div>
        </div>
      </div>

      {/* Form Section */}
      <div className="bg-gray-50 py-16">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="px-6 py-8 sm:p-10">
              <div className="text-center mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-3">
                  Help us prioritize your integration
                </h2>
                <p className="text-gray-600">
                  Fill out the form below and we&apos;ll add your requested integration to our roadmap.
                </p>
              </div>

              <form onSubmit={handleSubmit(onSubmit as (data: unknown) => Promise<void>) as (e: React.FormEvent) => void} className="space-y-6">
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      {...register('name')}
                      className={`py-3 px-4 block w-full shadow-sm rounded-xl border ${
                        errors.name ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                      }`}
                      placeholder="Your full name"
                    />
                    {errors.name && (
                      <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      {...register('email')}
                      className={`py-3 px-4 block w-full shadow-sm rounded-xl border ${
                        errors.email ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                      }`}
                      placeholder="your@email.com"
                    />
                    {errors.email && (
                      <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div>
                    <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1">
                      Company/Agency Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="company"
                      {...register('company')}
                      className={`py-3 px-4 block w-full shadow-sm rounded-xl border ${
                        errors.company ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                      }`}
                      placeholder="Your agency name"
                    />
                    {errors.company && (
                      <p className="mt-1 text-sm text-red-600">{errors.company.message}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      {...register('phone')}
                      className="py-3 px-4 block w-full shadow-sm border-gray-300 rounded-xl border focus:ring-blue-500 focus:border-blue-500"
                      placeholder="555-123-4567"
                    />
                    {errors.phone && (
                      <p className="mt-1 text-sm text-red-600">{errors.phone.message}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label htmlFor="integration" className="block text-sm font-medium text-gray-700 mb-1">
                    Which Integration Do You Need? <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="integration"
                    {...register('integration')}
                    className={`py-3 px-4 block w-full shadow-sm rounded-xl border ${
                      errors.integration ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                    }`}
                    placeholder="e.g., Applied Epic, AgencyZoom, AMS360, QQCatalyst, etc."
                  />
                  {errors.integration && (
                    <p className="mt-1 text-sm text-red-600">{errors.integration.message}</p>
                  )}
                  <p className="mt-1 text-sm text-gray-500">
                    Tell us which AMS, CRM, or other system you&apos;d like us to integrate with
                  </p>
                </div>

                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                    Additional Details <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="description"
                    rows={4}
                    {...register('description')}
                    className={`py-3 px-4 block w-full shadow-sm rounded-xl border ${
                      errors.description ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                    }`}
                    placeholder="Please describe your specific needs, current workflow, or any other details that would help us build the right integration for you..."
                  />
                  {errors.description && (
                    <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>
                  )}
                  <p className="mt-1 text-sm text-gray-500">
                    Help us understand your needs better
                  </p>
                </div>

                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full flex justify-center py-3 px-4 border border-transparent rounded-full shadow-sm text-base font-medium text-white transition-all hover:transform hover:scale-[1.01] hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                    style={{ backgroundColor: primaryColor }}
                  >
                    {isSubmitting ? 'Submitting Request...' : 'Submit Integration Request'}
                  </button>
                </div>
              </form>

              <div className="mt-8 pt-6 border-t border-gray-100 text-center">
                <p className="text-sm text-gray-500">
                  Want to see Voley in action first?{' '}
                  <Link href="/request-demo" className="font-medium hover:underline" style={{ color: primaryColor }}>
                    Schedule a demo
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-br from-brand/75 via-brand-green/75 to-brand-yellow/75">
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:py-16 lg:px-8 lg:flex lg:items-center lg:justify-between">
          <h2 className="text-3xl sm:text-4xl font-medium tracking-tight text-white">
            <span className="block">Already have questions?</span>
            <span className="block opacity-90">We&apos;re here to help.</span>
          </h2>
          <div className="mt-8 flex lg:mt-0 lg:flex-shrink-0">
            <div className="inline-flex">
              <Link
                href="/contact"
                className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-full text-gray-900 bg-white hover:bg-slate-50 transition-all hover:transform hover:scale-[1.02] hover:shadow-lg"
              >
                Contact our team
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
